import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Quotation, STATUS_COLORS, STATUS_OPTIONS, formatQuotationDate } from '@/lib/quotation-types';
import { ChevronDown, ChevronUp, Loader as Loader2, Package, MapPin, Truck, ClipboardList, Trash2 } from 'lucide-react';

interface GroupedQuotations {
  resellerId: string | null;
  resellerName: string;
  quotations: Quotation[];
}


interface QuotationRowProps {
  q: Quotation;
  onStatusChange: (id: string, status: string) => void;
  onDelete: (id: string) => void;
}

function QuotationRow({ q, onStatusChange, onDelete }: QuotationRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleStatus = async (status: string) => {
    setUpdatingStatus(true);
    await onStatusChange(q.id, status);
    setUpdatingStatus(false);
  };

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setDeleting(true);
    await onDelete(q.id);
  };

  const bom = q.bom_snapshot;

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
      <div
        className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => { setExpanded(v => !v); setConfirmDelete(false); }}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-800 text-sm">{q.company}</span>
            <span className="text-xs text-slate-400">/</span>
            <span className="text-sm text-slate-600">{q.contact_name}</span>
            <span className="text-xs text-slate-400">{q.email}</span>
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-xs text-slate-500">{formatQuotationDate(q.created_at)}</span>
            {q.user_email && (
              <span className="text-xs text-slate-400">by {q.user_email}</span>
            )}
            {bom && (
              <span className="text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded">
                €{bom.totalPrice.toFixed(2)}
              </span>
            )}
            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${STATUS_COLORS[q.status] ?? 'bg-slate-100 text-slate-600'}`}>
              {q.status}
            </span>
          </div>
        </div>
        <div className="shrink-0 text-slate-400 mt-0.5">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-100 px-4 py-4 space-y-4 bg-slate-50">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-slate-400" />
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Delivery details</p>
              </div>
              <div className="text-sm text-slate-700 space-y-0.5">
                {q.address && <p>{q.address}</p>}
                <p>{[q.postal_code, q.city].filter(Boolean).join(' ')}</p>
                <p>{q.country}</p>
                {q.phone && <p className="text-slate-500">{q.phone}</p>}
              </div>
              <div className="flex items-center gap-1.5 mt-1">
                <Truck className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-xs text-slate-600 capitalize">{q.shipping_method}</span>
              </div>
              {q.notes && (
                <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-600 italic">
                  {q.notes}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <ClipboardList className="h-3.5 w-3.5 text-slate-400" />
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Status</p>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {STATUS_OPTIONS.map(s => (
                  <button
                    key={s}
                    disabled={updatingStatus || q.status === s}
                    onClick={e => { e.stopPropagation(); handleStatus(s); }}
                    className={`px-3 py-1 text-xs font-semibold rounded-lg transition-colors ${
                      q.status === s
                        ? STATUS_COLORS[s]
                        : 'bg-white border border-slate-200 text-slate-500 hover:border-slate-400'
                    } disabled:opacity-50`}
                  >
                    {s}
                  </button>
                ))}
                {updatingStatus && <Loader2 className="h-4 w-4 animate-spin text-slate-400 ml-1" />}
              </div>

              <div className="pt-2 border-t border-slate-200 mt-3">
                {confirmDelete ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-red-600 font-medium">Delete this request?</span>
                    <button
                      disabled={deleting}
                      onClick={e => { e.stopPropagation(); handleDelete(); }}
                      className="px-3 py-1 text-xs font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Confirm'}
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); setConfirmDelete(false); }}
                      className="px-3 py-1 text-xs font-semibold rounded-lg bg-white border border-slate-200 text-slate-500 hover:border-slate-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={e => { e.stopPropagation(); handleDelete(); }}
                    className="flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-lg text-red-500 border border-red-200 hover:bg-red-50 hover:border-red-400 transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete request
                  </button>
                )}
              </div>
            </div>
          </div>

          {bom && bom.items.length > 0 && (
            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <Package className="h-3.5 w-3.5 text-slate-400" />
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  Bill of Materials — {bom.totalWeight} kg · €{bom.totalPrice.toFixed(2)}
                </p>
              </div>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-slate-600">ID</th>
                      <th className="text-left px-3 py-2 font-medium text-slate-600">Description</th>
                      <th className="text-center px-3 py-2 font-medium text-slate-600">Unit</th>
                      <th className="text-right px-3 py-2 font-medium text-slate-600">Qty</th>
                      <th className="text-right px-3 py-2 font-medium text-slate-600">Unit price</th>
                      <th className="text-right px-3 py-2 font-medium text-slate-600">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {bom.items.map((item, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-3 py-1.5 font-mono text-slate-400">{item.materialId}</td>
                        <td className="px-3 py-1.5 text-slate-700">{item.description}</td>
                        <td className="px-3 py-1.5 text-center text-slate-500">{item.unit}</td>
                        <td className="px-3 py-1.5 text-right font-semibold text-slate-800">{item.quantity}</td>
                        <td className="px-3 py-1.5 text-right text-slate-600">€{item.unitPrice.toFixed(2)}</td>
                        <td className="px-3 py-1.5 text-right font-semibold text-slate-800">€{item.totalPrice.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 border-t border-slate-200">
                    <tr>
                      <td colSpan={5} className="px-3 py-2 text-right font-bold text-slate-700">Total:</td>
                      <td className="px-3 py-2 text-right font-bold text-green-700">€{bom.totalPrice.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function QuotationsTab() {
  const [groups, setGroups] = useState<GroupedQuotations[]>([]);
  const [loading, setLoading] = useState(true);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const fetchQuotations = async () => {
    setLoading(true);

    const { data: quotations, error } = await supabase
      .from('quotation_requests')
      .select('*')
      .order('created_at', { ascending: false });

    if (error || !quotations) { setLoading(false); return; }

    const resellerIds = [...new Set(quotations.map(q => q.reseller_id).filter(Boolean))];
    const userIds = [...new Set(quotations.map(q => q.user_id).filter(Boolean))];

    const [resellersResult, profilesResult] = await Promise.all([
      resellerIds.length > 0
        ? supabase.from('resellers').select('id, name').in('id', resellerIds)
        : { data: [] },
      userIds.length > 0
        ? supabase.from('profiles').select('id, email').in('id', userIds)
        : { data: [] },
    ]);

    const resellerMap: Record<string, string> = {};
    for (const r of resellersResult.data ?? []) resellerMap[r.id] = r.name;

    const userEmailMap: Record<string, string> = {};
    for (const p of profilesResult.data ?? []) {
      if (p.email) userEmailMap[p.id] = p.email;
    }

    const enriched: Quotation[] = quotations.map(q => ({
      ...q,
      reseller_name: q.reseller_id ? (resellerMap[q.reseller_id] ?? 'Unknown reseller') : 'No reseller',
      user_email: q.user_id ? (userEmailMap[q.user_id] ?? undefined) : undefined,
    }));

    const groupMap: Record<string, GroupedQuotations> = {};
    for (const q of enriched) {
      const key = q.reseller_id ?? '__none__';
      if (!groupMap[key]) {
        groupMap[key] = {
          resellerId: q.reseller_id,
          resellerName: q.reseller_name ?? 'No reseller',
          quotations: [],
        };
      }
      groupMap[key].quotations.push(q);
    }

    setGroups(Object.values(groupMap).sort((a, b) => a.resellerName.localeCompare(b.resellerName)));
    setLoading(false);
  };

  useEffect(() => { fetchQuotations(); }, []);

  const handleStatusChange = async (id: string, status: string) => {
    await supabase.from('quotation_requests').update({ status }).eq('id', id);
    await fetchQuotations();
  };

  const handleDelete = async (id: string) => {
    await supabase.from('quotation_requests').delete().eq('id', id);
    await fetchQuotations();
  };

  const toggleGroup = (resellerId: string | null) => {
    const key = resellerId ?? '__none__';
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const totalCount = groups.reduce((s, g) => s + g.quotations.length, 0);

  const filteredGroups = groups.map(g => ({
    ...g,
    quotations: statusFilter === 'all' ? g.quotations : g.quotations.filter(q => q.status === statusFilter),
  })).filter(g => g.quotations.length > 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-semibold text-slate-800">
          Quotation Requests
          {!loading && (
            <span className="ml-2 text-sm font-normal text-slate-400">({totalCount} total)</span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Filter:</span>
          <div className="flex gap-1">
            {['all', ...STATUS_OPTIONS].map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1 text-xs font-semibold rounded-lg transition-colors ${
                  statusFilter === s
                    ? 'bg-[#212b4f] text-white'
                    : 'bg-white border border-slate-200 text-slate-500 hover:border-slate-400'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : filteredGroups.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Package className="h-8 w-8 mx-auto mb-3 text-slate-200" />
          <p className="text-sm">No quotation requests{statusFilter !== 'all' ? ` with status "${statusFilter}"` : ''}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {filteredGroups.map(group => {
            const key = group.resellerId ?? '__none__';
            const collapsed = collapsedGroups.has(key);
            const pendingCount = group.quotations.filter(q => q.status === 'pending').length;

            return (
              <div key={key}>
                <button
                  onClick={() => toggleGroup(group.resellerId)}
                  className="w-full flex items-center gap-3 mb-2 text-left group"
                >
                  <div className="flex items-center gap-2 flex-1">
                    <span className="text-sm font-semibold text-slate-700 group-hover:text-slate-900 transition-colors">
                      {group.resellerName}
                    </span>
                    <span className="text-xs text-slate-400 font-normal">
                      {group.quotations.length} request{group.quotations.length !== 1 ? 's' : ''}
                    </span>
                    {pendingCount > 0 && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700">
                        {pendingCount} pending
                      </span>
                    )}
                  </div>
                  {collapsed ? (
                    <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
                  ) : (
                    <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" />
                  )}
                </button>

                {!collapsed && (
                  <div className="space-y-2">
                    {group.quotations.map(q => (
                      <QuotationRow
                        key={q.id}
                        q={q}
                        onStatusChange={handleStatusChange}
                        onDelete={handleDelete}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
